import { Col, Form, InputNumber, Row, message } from "antd"
import { useCallback, useEffect, useState } from "react"

import ButtonLink from "components/theme_components/ButtonLink"
import FormInput from "components/FormInput"
import ParListSelect from "components/ParListSelect"
import RateFormulaSelectByCriteria from "components/RateFormulaSelectByCriteria"
import type React from "react"

type ProductTauxModalProps = {
  setVisible: (visible: boolean) => void
  prefsteplist: any[]
  onSaveRate?: (rate: any) => void
  isCreate?: boolean
  data?: any
  durationmax?: number
  franchiseperiod?: number
}
 
const ProductRateModal: React.FC<ProductTauxModalProps> = ({
  setVisible,
  prefsteplist,
  onSaveRate,
  isCreate,
  data,
  durationmax = 0,
  franchiseperiod = 0,
}) => {
  const [form] = Form.useForm()
  const [disableNscFields, setDisableNscFields] = useState<boolean>(false)
  
  // AJOUT: États pour stocker le code et le label de la formule client
  const [clientRateFormulaCode, setClientRateFormulaCode] = useState<string>('');
  const [clientRateFormulaLabel, setClientRateFormulaLabel] = useState<string>('');
 
  const updateEndDay = useCallback(() => {
    const values = form.getFieldsValue()
    const startDay = Number(values.ratestartday)
    const nbDays = Number(values.ratenbdaysmax)
 
    if (startDay && nbDays) {
      const calculatedEndDay = startDay + nbDays
      const finalEndDay = Math.min(calculatedEndDay, durationmax)
 
      console.log("Updating rateendday:", finalEndDay)
      form.setFieldsValue({ rateendday: finalEndDay })
 
      // If the calculated end day exceeds duration max, adjust ratenbdaysmax
      if (calculatedEndDay > durationmax) {
        const adjustedNbDays = durationmax - startDay
        console.log("Adjusting ratenbdaysmax to:", adjustedNbDays)
        form.setFieldsValue({ ratenbdaysmax: Math.max(1, adjustedNbDays) })
      }
    }
  }, [form, durationmax])
 
  const ratestartday = Form.useWatch("ratestartday", form)
  const ratenbdaysmax = Form.useWatch("ratenbdaysmax", form)
 

  const ratecodespread = form.getFieldValue("ratecodespread")
 
  const handleSubmit = async () => {
    try {
      const values = await form.validateFields()
      const startDay = Number(values.ratestartday)
      const nbDays = Number(values.ratenbdaysmax)
      const endDay = Number(values.rateendday)
 
      // Get the actual values from the form
      const nscappliedrate = Number(form.getFieldValue("nscappliedrate") || 0)
      const clientappliedrate = Number(form.getFieldValue("clientappliedrate") || 0)
      const nscrateformulaValue = form.getFieldValue("nscrateformula")
      const rateformulaValue = clientRateFormulaCode || form.getFieldValue("rateformula")
      const rateformulaLabelValue = clientRateFormulaLabel || form.getFieldValue("rateformula")
      const nscbillingcondition = form.getFieldValue("nscbillingcondition")
      const clibillingcondition = form.getFieldValue("clibillingcondition")
 
      if (isCreate) {
        let nextPeriodNumber = 1
        if (prefsteplist.length > 0) {
          nextPeriodNumber = Math.max(...prefsteplist.map((item) => Number(item.nsc?.ctesorder || 0))) + 1
        }
 
        const baseKey = prefsteplist.length === 0 ? 1 : Math.max(...prefsteplist.map((p) => p.nsc?.key || 0)) + 1
 
        const nscStep = {
          key: baseKey,
          ctesorder: nextPeriodNumber,
          ratestartday: startDay,
          ratenbdaysmax: nbDays,
          rateendday: endDay,
          nscrateformula: nscrateformulaValue || null,
          nscappliedrate: nscappliedrate || null,
          clibillingcondition: null,
          nscbillingcondition: nscbillingcondition || null,
          rateformula: null,
          clientappliedrate: null,
          prefinsteptype: "PART",
        }
 
        const clientStep = {
          key: baseKey + 1,
          ctesorder: nextPeriodNumber,
          ratestartday: startDay,
          ratenbdaysmax: nbDays,
          rateendday: endDay,
          nscrateformula: null,
          nscappliedrate: null,
          clibillingcondition: clibillingcondition,
          nscbillingcondition: clibillingcondition || "EOP",
          rateformula: rateformulaValue || null,
          rateformulalabel: rateformulaLabelValue || null, // Add label for display
          clientappliedrate,
          prefinsteptype: "INT",
        }
 
        console.log("Creating new rate period:", { nscStep, clientStep })
        onSaveRate?.([nscStep, clientStep])
      } else {
        // Edit mode
        const periodNumber = data?.nsc?.ctesorder || 1
        const baseKey = data?.nsc?.key || 1
 
        const nscStep = {
          key: baseKey,
          ctesorder: periodNumber,
          ratestartday: startDay,
          ratenbdaysmax: nbDays,
          rateendday: endDay,
          nscrateformula: nscrateformulaValue || null,
          nscappliedrate: nscappliedrate || null,
          clibillingcondition: null,
          nscbillingcondition: nscbillingcondition || null,
          rateformula: null,
          clientappliedrate: null,
          prefinsteptype: "PART",
        }
 
        const clientStep = {
          key: baseKey + 1,
          ctesorder: periodNumber,
          ratestartday: startDay,
          ratenbdaysmax: nbDays,
          rateendday: endDay,
          nscrateformula: null,
          nscappliedrate: null,
          clibillingcondition: clibillingcondition,
          nscbillingcondition: clibillingcondition || "EOP",
          rateformula: rateformulaValue || null,
          rateformulalabel: rateformulaLabelValue || null, // Add label for display
          clientappliedrate,
          prefinsteptype: "INT",
        }
 
        console.log("Updating rate period:", { nscStep, clientStep })
        onSaveRate?.([nscStep, clientStep])
      }
 
      setVisible(false)
    } catch (error) {
      console.error("Form validation failed:", error)
    }
  }
 
  useEffect(() => {
    form.resetFields()
 
    if (data) {
      const formValues = {
        ratestartday: data?.nsc?.ratestartday || 1,
        ratenbdaysmax: data?.nsc?.ratenbdaysmax || 1,
        rateendday: data?.nsc?.rateendday, // Set default to durationmax
        nscrateformula: data?.nsc?.nscrateformula || null,
        nscappliedrate: data?.nsc?.nscappliedrate || 0,
        nscbillingcondition: data?.nsc?.nscbillingcondition || "",
        clibillingcondition: data?.client?.clibillingcondition || "EOP",
        // Use label if available, otherwise use code
        rateformula: data?.client?.rateformulalabel || data?.client?.rateformula || null,
        clientappliedrate: data?.client?.clientappliedrate || 0,
      }
 
      console.log("Setting form values for edit:", formValues)
      form.setFieldsValue(formValues)
      
      setClientRateFormulaCode(data?.client?.rateformula || ''); // Store code separately
      setClientRateFormulaLabel(data?.client?.rateformulalabel || data?.client?.rateformula || ''); // Store label, fallback to code if no label
 
      // Determine if NSC fields should be disabled based on franchise period
      const startDay = data?.nsc?.ratestartday || 1
      const endDay = startDay + (data?.nsc?.ratenbdaysmax || 0)
      setDisableNscFields(endDay > franchiseperiod)
    } else if (isCreate) {
      // Create mode
      let startDay = 1
      let availableDays = durationmax - startDay 
 
      if (prefsteplist.length > 0) {
        const lastItem = prefsteplist[prefsteplist.length - 1]
        const lastEndDay = Number(lastItem.nsc?.rateendday || 0)
        startDay = lastEndDay + 1
        availableDays = durationmax - lastEndDay
      }
 
      form.setFieldsValue({
        ratestartday: startDay,
        // ratenbdaysmax: availableDays > 0 ? availableDays : 1,
        // rateendday: durationmax, // Set default to durationmax
      })
 
      // Determine if NSC fields should be disabled based on franchise period
      const endDay = startDay + (availableDays > 0 ? availableDays : 1)
 
      setDisableNscFields(endDay > franchiseperiod)
      
      setClientRateFormulaCode('');
      setClientRateFormulaLabel('');
      
      console.log("disableFields:", disableNscFields)
    }
  }, [data, isCreate])
 
  // Handle ratestartday change with validation
 const handleRateStartDayChange = (value: number | null) => {
  // When start day changes, recalculate rate days max so that end day stays the same
  const endDay = Number(form.getFieldValue("rateendday"));
  if (value != null && endDay) {
    const newNbDays = endDay - value;
    form.setFieldsValue({
      ratestartday: value,
      ratenbdaysmax: newNbDays > 0 ? newNbDays : 1,
    });
  }
};
 
const [isNbDaysValid, setIsNbDaysValid] = useState(true);

const handleRateNbDaysChange = (value: number | null) => {
  const startDay = Number(form.getFieldValue("ratestartday"));
  if (value != null && startDay) {
    let newEndDay = startDay +value -1;
    if (newEndDay >= durationmax) {
      // Auto-correct to max allowed
      const correctedNbDays = durationmax - startDay + 1;
      form.setFieldsValue({
        ratenbdaysmax: correctedNbDays,
        rateendday: durationmax,
      });
      setIsNbDaysValid(false);
      form.setFields([
        {
          name: "ratenbdaysmax",
          //errors: [`Le nombre de jours est trop grand, il a été corrigé à ${correctedNbDays}`],
        },
      ]);
    } else {
      form.setFieldsValue({
        ratenbdaysmax: value,
        rateendday: newEndDay,
      });
      setIsNbDaysValid(true);
      form.setFields([
        {
          name: "ratenbdaysmax",
          errors: [],
        },
      ]);
    }
  }
};
 
  return (
    <Form form={form} layout="vertical" className="no-border-radius ut-wizard-form">
      <Row gutter={16}>
        <Col span={8}>
          <FormInput name="ratestartday" rules={[{ required: true, message: "Veuillez saisir un jour de début" }]}>
            <InputNumber
              min={1}
              max={durationmax}
              className="no-border-radius ut-wizard-form color-gray"
              onChange={handleRateStartDayChange}
            />
          </FormInput>
        </Col>
        <Col span={8}>
          <FormInput name="ratenbdaysmax" rules={[{ required: true, message: "Veuillez saisir le nombre de jours" }]}>
            <InputNumber
              min={1}
              className="no-border-radius ut-wizard-form color-gray"
              onChange={handleRateNbDaysChange}
            />
          </FormInput>
        </Col>
        <Col span={8}>
          <FormInput
            name="rateendday"
            rules={[
              {
                validator: (_, value) =>
                  value <= durationmax ? Promise.resolve() : Promise.reject("Veuillez saisir un jour de fin valide"),
              },
            ]}
          >
            <InputNumber
              disabled // Always disabled
              className="no-border-radius ut-wizard-form color-gray"
            />
          </FormInput>
        </Col>
      </Row>
 
      <Row gutter={16}>
        <Col span={12}>
          <FormInput
            name="clibillingcondition"
            rules={[
              {
                required: disableNscFields,
                message: "Veuillez selectionner une condition",
              },
            ]}
          >
            <ParListSelect parcode="INTCALC" />
          </FormInput>
        </Col>
        <Col span={12}>
          <FormInput
            name="rateformula"
            rules={[
              {
                required: disableNscFields,
                message: "Veuillez selectionner la formule client",
              },
            ]}
          >
            <RateFormulaSelectByCriteria
              params={{
                formulalabel: "",
                formulacode: "",
                formulastatus: "",
                formulatype: "",
                ratecode: "",
                ratelabel: "",
                formuladescription: "",
                ratecodespread: ratecodespread ?? undefined,
              }}
              onSelected={(e) => {
                console.log("Selected client rate formula:", e)
                
                // Store both code and label separately
                setClientRateFormulaCode(e.formulacode);
                setClientRateFormulaLabel(e.formulalabel || e.formulacode);
                
                form.setFieldsValue({
                  clientappliedrate: e?.ratevalue + (e?.ratespread || 0),
                  // Store the label in the form field for display purposes
                  rateformula: e.formulalabel || e.formulacode,
                })
              }}
            />
          </FormInput>
        </Col>
      </Row>
 
      <Row justify="end" className="mt-7">
        <ButtonLink comcode="Annuler" className="ut-btn-bordred" onClick={() => setVisible(false)} />
        <ButtonLink
          type="primary"
          comcode="validate"
          htmlType="submit"
          className="ut-reset-btn"
          onClick={handleSubmit}
          iconPosition="end"
        />
      </Row>
    </Form>
  )
}
 
export default ProductRateModal